import { Anima } from "kido/anima";
import { ResizeHub } from "kido/resize";
import { Split } from "kido/split";

import { queryAll, bounds, lerp, Ease, clamp, damp, Sniff } from "kido/utils";
import { WheelKeySubscription } from "kido/wheel";

import { App, type GpuInstance, type RouteMediaSlot } from "@app/context";
import { out } from "@app/controller";
import { BasePage } from "@app/primitives/base-page";

import type Scene from "../gpu/scene";
import type { ScenePlane } from "../gpu/scene";

const SCROLL_THRESHOLD = 450;
const SCROLL_THRESHOLD_MOBILE = 75;
const SCROLL_SNAP_DAMP = 0.03;
const SCROLL_SNAP_DAMP_MOBILE = 0.025;
const SCROLL_ACTIVE_DAMP = 0.06;
const SCROLL_ACTIVE_DAMP_MOBILE = 0.4;
const SCROLL_IDLE_MS = 350;
const SCROLL_IDLE_MS_MOBILE = 500;

const DECK_CARD_W_RATIO = 0.18;
const DECK_STAGGER_MS = 80;
const DECK_FLY_DUR = 1200;
const DECK_SCALE_DUR = 1100;
const DECK_GAP = 0.3;
const DECK_DWELL_MS = 150;
const DECK_DELAY_MS = 100;

export class HomePage extends BasePage {
  private gpu: GpuInstance | null;
  private plane: ScenePlane | null;
  private _slot: Element | null;
  private _roId: symbol | null;
  private _isIntro: boolean;
  private _translateFrom: { x: number; y: number; w: number; h: number } | null;
  private _translateTarget: {
    x: number;
    y: number;
    w: number;
    h: number;
  } | null;
  private _scaleTarget: { x: number; y: number; w: number; h: number } | null;
  private _introStart: number;
  private _inDone: (() => void) | null;

  private _scrollTarget: number = 0;
  private _scrollCurrent: number = 0;
  private _scrollActive: boolean = false;
  private _navigating: boolean = false;
  private _wheelSub: WheelKeySubscription | null = null;
  private _touchStartY: number = 0;
  private _lastDeltaTime: number = 0;
  private _fadeEls: HTMLElement[] = [];
  private _bgEl: HTMLElement | null = null;
  private _arrowEl: HTMLElement | null = null;
  private _textRevealed: boolean = false;

  private _onTouchStart: ((e: TouchEvent) => void) | null = null;
  private _onTouchMove: ((e: TouchEvent) => void) | null = null;
  private _mobileScrollRaf: number = 0;
  private _touchBlocker: ((e: TouchEvent) => void) | null = null;

  // Deck animation state
  private _deckPlanes: ScenePlane[] = [];
  private _deckAllCards: ScenePlane[] = [];
  private _hasDeck: boolean = false;
  private _deckPhase: 0 | 1 | 2 | 3 = 0;
  private _deckStart: number = 0;
  private _deckSpreadPositions: Array<{
    x: number;
    y: number;
    w: number;
    h: number;
    rz: number;
  }> = [];
  private _homePlaneIndex: number = -1;
  private _heroIsSwapped: boolean = false;

  constructor() {
    super();
    this.gpu = App?.gpu;
    this.plane = null;
    this._slot = null;
    this._roId = null;
    this._isIntro = false;
    this._translateFrom = null;
    this._translateTarget = null;
    this._scaleTarget = null;
    this._introStart = 0;
    this._inDone = null;
  }

  async init(container: Element | null): Promise<void> {
    if (!container) return;
    await super.init(container);
    this._isIntro = App.route.old.url === null;
    if (this.gpu) {
      this.gpu.freezeBounds?.(true);
    }
    this.initIntroAnimation();

    this._arrowEl = container.querySelector(".l-c_arrow") as HTMLElement | null;
    if (this._arrowEl) {
      this._arrowEl.addEventListener("click", (e) => {
        e.preventDefault();
        if (App.mutating) return;
        App.transitState = { slug: "", direction: "to-projects" };
        out("/projects");
      });
    }
  }

  async in(): Promise<void> {
    // Block native touch scroll for the entire home page lifetime on mobile
    if (!this.gpu) {
      this._touchBlocker = (e: TouchEvent) => e.preventDefault();
      document.addEventListener("touchmove", this._touchBlocker, {
        passive: false,
      });
    }

    const scene = this.gpu?.scenes["/"] as Scene | undefined;
    if (scene) {
      await Promise.race([
        scene.videoReady,
        new Promise<void>((resolve) => setTimeout(resolve, 3000)),
      ]);
    }

    document.body.style.overflow = "hidden";
    App.scroller?.pause?.();

    if (this._isIntro) {
      await App.introSwipe;
    }

    // Set up deck after intro swipe — textures are fully loaded by now
    this._setupDeck();

    if (this.gpu && (this.plane?.boundsPx || this._hasDeck)) {
      this._textRevealed = false;
      await new Promise<void>((r) => setTimeout(r, DECK_DELAY_MS));
      await new Promise<void>((resolve) => {
        this._inDone = resolve;
        this._introStart = performance.now();
        if (this._hasDeck) {
          this._deckStart = performance.now();
        }
        this.gpu!.setPreRender?.(() => this._introFrame());
      });
    }

    if (!this._textRevealed) {
      await this._revealText(0);
    }
  }

  async out(): Promise<void> {
    if (this._touchBlocker) {
      document.removeEventListener("touchmove", this._touchBlocker);
      this._touchBlocker = null;
    }
    this._cleanupDeckPlanes();
    if (this._heroIsSwapped && this.plane) {
      const scene = this.gpu?.scenes["/"];
      const scenePlanes = (scene as any)?.planes as ScenePlane[] | undefined;
      if (scenePlanes) {
        const idx = scenePlanes.indexOf(this.plane);
        if (idx !== -1) scenePlanes.splice(idx, 1);
      }
      this._heroIsSwapped = false;
    }
    this._deactivateScroll();
    if (this._roId !== null) {
      ResizeHub.remove(this._roId);
      this._roId = null;
    }
    this.gpu?.setPreRender?.(null);
    if (!this._navigating) {
      this.gpu?.freezeBounds?.(false);
    }
    document.body.style.overflow = "";
    App.scroller?.resume?.();
  }

  // ── Deck setup ──────────────────────────────────────────────────────

  private initIntroAnimation(): void {
    if (!this.gpu) return;

    const scene = this.gpu.scenes["/"];
    const planes: ScenePlane[] = (scene as any)?.planes ?? [];
    this.plane = planes[0] ?? null;
    this._slot = (scene as any)?.domSlots?.[0] ?? null;
    if (this.plane) this.plane.borderRadius = 8;
    this.resizePlane();
    this._roId = ResizeHub.add(() => this.resizePlane());
  }

  /** Create deck planes from loaded cover textures. Called from in() after textures are ready. */
  private _setupDeck(): void {
    if (!this.gpu || !this.plane) return;

    const scene = this.gpu.scenes["/"];
    const coverSlots: RouteMediaSlot[] = (
      (
        App.gpu?.media as
          | Record<string, Record<string, RouteMediaSlot[]>>
          | undefined
      )?.["/"]?.covers ?? []
    ).filter((slot) => !!slot.attrib[0]);

    if (coverSlots.length > 0) {
      this._deckPlanes = coverSlots.map((slot) => ({
        texture: slot.attrib[0],
        sampler: slot.sampler,
        aspectRatio: slot.wh || 1,
        opacity: 0,
        boundsPx: { x: -9999, y: -9999, w: 0, h: 0, z: 1 },
        borderRadius: 8,
        strength: 0,
        strengthEased: 0,
        hoverTilt: 0,
        hoverTiltEased: 0,
        hoverZoom: 0,
        hoverZoomEased: 0,
      }));

      const totalN = this._deckPlanes.length + 1;
      const homeSlot = Math.floor(totalN / 2);
      this._homePlaneIndex = homeSlot;

      // Build ordered card array: reel at center, covers around it
      this._deckAllCards = [];
      let coverIdx = 0;
      for (let i = 0; i < totalN; i++) {
        if (i === homeSlot) {
          this._deckAllCards.push(this.plane);
        } else {
          this._deckAllCards.push(this._deckPlanes[coverIdx++]!);
        }
      }

      // When randomizing, swap a random card into the center slot
      const randomizeHero =
        this.container?.getAttribute("data-randomize-hero") === "true";
      if (randomizeHero && this._deckPlanes.length > 0) {
        const randomIdx = Math.floor(Math.random() * totalN);
        if (randomIdx !== homeSlot) {
          const tmp = this._deckAllCards[homeSlot]!;
          this._deckAllCards[homeSlot] = this._deckAllCards[randomIdx]!;
          this._deckAllCards[randomIdx] = tmp;
          this._heroIsSwapped = true;
        }
      }

      // The center card is always the one that scales up
      const heroPlane = this._deckAllCards[homeSlot]!;
      this.plane = heroPlane;

      const scenePlanes = (scene as any)?.planes as ScenePlane[] | undefined;
      if (scenePlanes) {
        for (const dp of this._deckPlanes) {
          scenePlanes.push(dp);
        }
      }

      this._computeDeckLayout();

      const cardW = window.innerWidth * DECK_CARD_W_RATIO;
      const cardH = cardW / (this.plane.aspectRatio || 16 / 9);
      this.plane.boundsPx = {
        x: -cardW * 2,
        y: window.innerHeight / 2 - cardH / 2,
        w: cardW,
        h: cardH,
        z: 1,
      };
      this.plane.opacity = 0;
      this.plane.opacityTarget = 0;
      this.plane.rotation = [0, Math.PI / 6, 0];

      const homeSpread = this._deckSpreadPositions[homeSlot];
      if (homeSpread) {
        this._translateTarget = {
          x: homeSpread.x,
          y: homeSpread.y,
          w: homeSpread.w,
          h: homeSpread.h,
        };
      }

      if (this._slot) {
        const slotRect = bounds(this._slot);
        this._scaleTarget = {
          x: slotRect.left,
          y: slotRect.top,
          w: slotRect.width,
          h: slotRect.height,
        };
      }

      this._hasDeck = true;
      this._deckPhase = 1;
    } else {
      // Fallback: single-plane animation
      this._hasDeck = false;
      this._deckPhase = 3;

      if (this.plane?.boundsPx && this._slot) {
        const vh = window.innerHeight;
        const bp = this.plane.boundsPx;
        this._translateFrom = {
          x: bp.x + bp.w / 4,
          y: bp.y + bp.h / 4,
          w: bp.w / 2,
          h: bp.h / 2,
        };
        this._translateTarget = {
          x: bp.x,
          y: vh / 2 - bp.h / 2,
          w: bp.w,
          h: bp.h,
        };
        const slotRect = bounds(this._slot);
        this._scaleTarget = {
          x: slotRect.left,
          y: slotRect.top,
          w: slotRect.width,
          h: slotRect.height,
        };
        this.plane.opacity = 1;
        this.plane.opacityTarget = undefined;
      }
    }
  }

  // ── Deck helpers ────────────────────────────────────────────────────

  private _deckFlyInEnd(): number {
    return DECK_FLY_DUR + (this._deckAllCards.length - 1) * DECK_STAGGER_MS;
  }

  private _computeDeckLayout(): void {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const totalN = this._deckAllCards.length;
    if (totalN === 0) return;

    const cardW = vw * DECK_CARD_W_RATIO;
    const step = cardW * (1 + DECK_GAP);
    const totalW = (totalN - 1) * step + cardW;
    const startX = vw / 2 - totalW / 2;

    this._deckSpreadPositions = [];
    for (let i = 0; i < totalN; i++) {
      const h = cardW / (16 / 9);
      const x = startX + i * step;
      const y = vh / 2 - h / 2;
      const seed = ((i * 2654435761) >>> 0) / 4294967296;
      const maxRot = Math.PI / 12;
      const rz = (seed - 0.5) * 2 * maxRot;
      this._deckSpreadPositions.push({ x, y, w: cardW, h, rz });
    }
  }

  private _cleanupDeckPlanes(): void {
    if (this._deckPlanes.length === 0) return;

    const scene = this.gpu?.scenes["/"];
    const scenePlanes = (scene as any)?.planes as ScenePlane[] | undefined;
    if (scenePlanes) {
      for (const dp of this._deckPlanes) {
        if (dp === this.plane) continue;
        const idx = scenePlanes.indexOf(dp);
        if (idx !== -1) scenePlanes.splice(idx, 1);
      }
    }

    this._deckPlanes = [];
    this._deckAllCards = [];
    this._deckSpreadPositions = [];
    this._hasDeck = false;
    this._deckPhase = 0;
  }

  // ── Intro animation phases ─────────────────────────────────────────

  private _introFrame(): void {
    if (this._hasDeck) {
      if (this._deckPhase === 1) {
        this._introFrameFlyIn();
      } else if (this._deckPhase === 2) {
        // Dwell phase — hold the fanned spread for DECK_DWELL_MS
        const dwellElapsed = performance.now() - this._introStart;
        if (dwellElapsed >= DECK_DWELL_MS) {
          this._deckPhase = 3;
          this._introStart = performance.now();
        }
        this.gpu?.requestExtraFrames?.();
      } else {
        this._introFrameScaleUp();
      }
    } else {
      this._introFrameOriginal();
    }
  }

  /** Phase 1 — Cards fly in from off-screen left with staggered timing. */
  private _introFrameFlyIn(): void {
    const elapsed = performance.now() - this._deckStart;
    const ease = Ease.oQ ?? ((t: number) => t);
    const totalN = this._deckAllCards.length;
    let allSettled = true;

    for (let i = 0; i < totalN; i++) {
      const card = this._deckAllCards[i];
      const target = this._deckSpreadPositions[i];
      if (!card || !target) continue;

      const distFromCenter = Math.abs(i - this._homePlaneIndex);
      const maxDist = Math.max(
        this._homePlaneIndex,
        totalN - 1 - this._homePlaneIndex
      );
      const staggerOrder = maxDist - distFromCenter;
      const cardElapsed = elapsed - staggerOrder * DECK_STAGGER_MS;
      if (cardElapsed < 0) {
        allSettled = false;
        continue;
      }

      const t = clamp(cardElapsed / DECK_FLY_DUR, 0, 1);
      const et = ease(t);

      if (t < 1) allSettled = false;

      const startX = -(target.w * 2);
      const startY = target.y;

      if (!card.boundsPx) {
        card.boundsPx = {
          x: startX,
          y: startY,
          w: target.w,
          h: target.h,
          z: 1,
        };
      }
      card.boundsPx.x = lerp(startX, target.x, et);
      card.boundsPx.y = lerp(startY, target.y, et);
      card.boundsPx.w = target.w;
      card.boundsPx.h = target.h;

      card.rotation = [0, lerp(Math.PI / 6, 0, et), lerp(0, target.rz, et)];
      card.opacity = clamp(cardElapsed / 150, 0, 1);
    }

    if (allSettled) {
      for (let i = 0; i < totalN; i++) {
        const card = this._deckAllCards[i];
        const target = this._deckSpreadPositions[i];
        if (!card?.boundsPx || !target) continue;
        card.boundsPx.x = target.x;
        card.boundsPx.y = target.y;
        card.boundsPx.w = target.w;
        card.boundsPx.h = target.h;
        card.rotation = [0, 0, target.rz];
        card.opacity = 1;
      }

      // Transition to dwell phase — hold fanned spread before pushing out
      this._deckPhase = 2;
      this._introStart = performance.now();

      const homeSpread = this._deckSpreadPositions[this._homePlaneIndex];
      if (homeSpread && this.plane) {
        this._translateTarget = {
          x: homeSpread.x,
          y: homeSpread.y,
          w: homeSpread.w,
          h: homeSpread.h,
        };
        this.plane.borderRadius = 8;
      }

      if (this._slot) {
        const slotRect = bounds(this._slot);
        this._scaleTarget = {
          x: slotRect.left,
          y: slotRect.top,
          w: slotRect.width,
          h: slotRect.height,
        };
      }
    }

    this.gpu?.requestExtraFrames?.();
  }

  /** Phase 2 — Covers push outward while home plane scales to DOM slot. */
  private _introFrameScaleUp(): void {
    if (!this.plane?.boundsPx || !this._translateTarget || !this._scaleTarget)
      return;

    const elapsed = performance.now() - this._introStart;
    const ease = Ease.ioQ ?? ((t: number) => t);

    if (elapsed < DECK_SCALE_DUR) {
      const t = ease(clamp(elapsed / DECK_SCALE_DUR, 0, 1));

      const from = this._translateTarget;
      const to = this._scaleTarget;
      this.plane.boundsPx.x = lerp(from.x, to.x, t);
      this.plane.boundsPx.y = lerp(from.y, to.y, t);
      this.plane.boundsPx.w = lerp(from.w, to.w, t);
      this.plane.boundsPx.h = lerp(from.h, to.h, t);
      this.plane.borderRadius = lerp(8, 0, t);
      const homeRz = this._deckSpreadPositions[this._homePlaneIndex]?.rz ?? 0;
      this.plane.rotation = [0, 0, lerp(homeRz, 0, t)];

      const vw = window.innerWidth;
      const pushDist = vw * 0.8;
      for (let i = 0; i < this._deckAllCards.length; i++) {
        if (i === this._homePlaneIndex) continue;
        const card = this._deckAllCards[i];
        const target = this._deckSpreadPositions[i];
        if (!card?.boundsPx || !target) continue;

        const dir = i < this._homePlaneIndex ? -1 : 1;
        card.boundsPx.x = lerp(target.x, target.x + dir * pushDist, t);
        card.opacity = lerp(1, 0, t);
        card.rotation = [0, 0, lerp(target.rz, 0, t)];
      }

      // Start text reveal 300ms before scale-up finishes
      if (!this._textRevealed && elapsed >= DECK_SCALE_DUR - 300) {
        this._textRevealed = true;
        this._startTextReveal();
      }
    } else {
      this.plane.rotation = [0, 0, 0];
      this.plane.borderRadius = 0;
      this.plane.opacityTarget = undefined;

      const tt = this._translateTarget;
      this._translateFrom = {
        x: tt.x + tt.w / 4,
        y: tt.y + tt.h / 4,
        w: tt.w / 2,
        h: tt.h / 2,
      };

      this._cleanupDeckPlanes();

      if (this._roId !== null) {
        ResizeHub.remove(this._roId);
        this._roId = null;
      }
      this.gpu?.freezeBounds?.(false);
      document.body.style.overflow = "";
      App.scroller?.resume?.();

      this._fadeEls = queryAll(this.container, "._o") as HTMLElement[];
      this._bgEl = (queryAll(this.container, "._bg")[0] as HTMLElement) ?? null;

      this.gpu?.setPreRender?.(null);
      this._activateScroll();
      this._inDone?.();
      this._inDone = null;
      return;
    }

    this.gpu?.requestExtraFrames?.();
  }

  /** Fallback: original single-plane fly-up + scale animation (no deck). */
  private _introFrameOriginal(): void {
    if (
      !this.plane?.boundsPx ||
      !this._translateFrom ||
      !this._translateTarget ||
      !this._scaleTarget
    )
      return;

    const elapsed = performance.now() - this._introStart;
    const ease = Ease.ioQ ?? ((t: number) => t);

    if (elapsed < 1100) {
      const t = ease(clamp(elapsed / 1100, 0, 1));
      const from = this._translateFrom;
      const to = this._translateTarget;
      const w = lerp(from.w, to.w, t);
      const h = lerp(from.h, to.h, t);
      const cx = lerp(from.x + from.w / 4, to.x + to.w / 4, t);
      const cy = lerp(from.y + from.h / 4, to.y + to.h / 4, t);
      this.plane.boundsPx.x = cx - w / 4;
      this.plane.boundsPx.y = cy - h / 4;
      this.plane.boundsPx.w = w;
      this.plane.boundsPx.h = h;
      this.plane.rotation = [0, lerp(Math.PI / 2, 0, t), 0];
    } else if (elapsed < 2500) {
      const t = ease(clamp((elapsed - 1100) / 1100, 0, 1));
      const from = this._translateTarget;
      const to = this._scaleTarget;
      this.plane.boundsPx.x = lerp(from.x, to.x, t);
      this.plane.boundsPx.y = lerp(from.y, to.y, t);
      this.plane.boundsPx.w = lerp(from.w, to.w, t);
      this.plane.boundsPx.h = lerp(from.h, to.h, t);
      this.plane.borderRadius = lerp(8, 0, t);
      this.plane.rotation = [0, 0, 0];

      if (!this._textRevealed && elapsed >= 2000) {
        this._textRevealed = true;
        this._startTextReveal();
      }
    } else {
      this.plane.rotation = [0, 0, 0];
      this.plane.borderRadius = 0;

      if (this._roId !== null) {
        ResizeHub.remove(this._roId);
        this._roId = null;
      }
      this.gpu?.freezeBounds?.(false);
      document.body.style.overflow = "";
      App.scroller?.resume?.();

      this._fadeEls = queryAll(this.container, "._o") as HTMLElement[];
      this._bgEl = (queryAll(this.container, "._bg")[0] as HTMLElement) ?? null;

      this.gpu?.setPreRender?.(null);
      this._activateScroll();
      this._inDone?.();
      this._inDone = null;
      return;
    }

    this.gpu?.requestExtraFrames?.();
  }

  // ── Text reveal ─────────────────────────────────────────────────────

  private _startTextReveal(): void {
    // Split intro text by line using kido Split
    const textWrapper = this.container?.querySelector(
      ".l-c_t_b span"
    ) as HTMLElement | null;
    if (textWrapper) {
      textWrapper.style.opacity = "1";
      const paras = textWrapper.querySelectorAll("p");
      if (paras.length > 0) {
        const fragments: string[] = [];
        paras.forEach((p) => fragments.push(p.innerHTML));
        textWrapper.innerHTML = fragments.join("<br>");
      }
      const split = new Split(textWrapper);
      split.resize({
        tag: {
          start:
            '<span class="s__o"><span class="s__i" style="transform: translate3d(0,102%,0);">',
          end: "</span></span>",
        },
      });
    }

    // Make _in elements visible so split animation is seen
    const inEls = queryAll(this.container, "._in") as HTMLElement[];
    inEls.forEach((el) => {
      el.style.opacity = "1";
    });

    // Wrap h1 lines in split reveal wrappers
    const h1Lines = queryAll(this.container, ".l-c_t_b_h p") as HTMLElement[];
    h1Lines.forEach((p) => {
      const wrapper = document.createElement("span");
      wrapper.className = "s__o";
      const inner = document.createElement("span");
      inner.className = "s__i";
      inner.style.transform = "translate3d(0,102%,0)";
      inner.style.display = "inline-block";
      while (p.firstChild) inner.appendChild(p.firstChild);
      wrapper.appendChild(inner);
      p.appendChild(wrapper);
    });

    // Wrap info label in split reveal wrapper
    const infoP = this.container?.querySelector(
      ".l-c_t_info > p"
    ) as HTMLElement | null;
    if (infoP) {
      const wrapper = document.createElement("span");
      wrapper.className = "s__o";
      const inner = document.createElement("span");
      inner.className = "s__i";
      inner.style.transform = "translate3d(0,102%,0)";
      inner.style.display = "inline-block";
      while (infoP.firstChild) inner.appendChild(infoP.firstChild);
      wrapper.appendChild(inner);
      infoP.appendChild(wrapper);
    }

    // Wrap info email in split reveal wrapper
    const infoEmail = this.container?.querySelector(
      ".l-c_t_info_email"
    ) as HTMLElement | null;
    if (infoEmail) {
      const wrapper = document.createElement("span");
      wrapper.className = "s__o";
      const inner = document.createElement("span");
      inner.className = "s__i";
      inner.style.transform = "translate3d(0,102%,0)";
      inner.style.display = "inline-block";
      while (infoEmail.firstChild) inner.appendChild(infoEmail.firstChild);
      wrapper.appendChild(inner);
      infoEmail.appendChild(wrapper);
    }

    // Wrap each sticker link itself in split reveal wrapper
    const stickers = queryAll(
      this.container,
      ".l-c_t_info_links .sticker"
    ) as HTMLElement[];
    stickers.forEach((sticker) => {
      const wrapper = document.createElement("span");
      wrapper.className = "s__o";
      wrapper.style.overflow = "hidden";
      const inner = document.createElement("span");
      inner.className = "s__i";
      inner.style.transform = "translate3d(0,102%,0)";
      inner.style.display = "inline-block";
      sticker.parentNode!.insertBefore(wrapper, sticker);
      inner.appendChild(sticker);
      wrapper.appendChild(inner);
    });

    // Split each nav link by line
    const linkEls = queryAll(this.container, ".l-c_t_n li") as HTMLElement[];
    linkEls.forEach((li) => {
      const wrapper = document.createElement("span");
      wrapper.className = "s__o";
      const inner = document.createElement("span");
      inner.className = "s__i";
      inner.style.transform = "translate3d(0,102%,0)";
      inner.style.display = "inline-block";
      while (li.firstChild) inner.appendChild(li.firstChild);
      wrapper.appendChild(inner);
      li.appendChild(wrapper);
    });

    // Collect all split line nodes
    const splitLines = queryAll(this.container, ".s__i") as HTMLElement[];

    // Reveal bg
    const bgEl = (queryAll(this.container, "._bg")[0] as HTMLElement) ?? null;
    if (bgEl) {
      bgEl.style.opacity = "0";
      new Anima({ el: bgEl, p: { o: [0, 1, ""] }, d: 600, e: "o1" }).play();
    }

    // Stagger-reveal each split line
    splitLines.forEach((line, i) => {
      new Anima({
        el: line,
        p: { y: [102, 0, "%"] },
        d: 1000,
        de: 100 + i * 80,
        e: "oQ",
      }).play();
    });
  }

  private _revealText(delay: number): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.gpu) {
        // Unfreeze so GPU plane follows DOM slot during scroll
        if (this._roId !== null) {
          ResizeHub.remove(this._roId);
          this._roId = null;
        }
        this.gpu.freezeBounds?.(false);
      }
      document.body.style.overflow = "";
      App.scroller?.resume?.();

      this._fadeEls = queryAll(this.container, "._o") as HTMLElement[];
      this._bgEl = (queryAll(this.container, "._bg")[0] as HTMLElement) ?? null;

      // Reveal bg first
      if (this._bgEl) {
        (this._bgEl as HTMLElement).style.opacity = "0";
        new Anima({
          el: this._bgEl,
          p: { o: [0, 1, ""] },
          d: 600,
          de: delay,
          e: "o1",
        }).play();
      }

      // Split intro text by line using kido Split
      const textWrapper = this.container?.querySelector(
        ".l-c_t_b span"
      ) as HTMLElement | null;
      if (textWrapper) {
        textWrapper.style.opacity = "1";
        // Unwrap <p> tags into inline text with <br> so Split can process them
        const paras = textWrapper.querySelectorAll("p");
        if (paras.length > 0) {
          const fragments: string[] = [];
          paras.forEach((p) => fragments.push(p.innerHTML));
          textWrapper.innerHTML = fragments.join("<br>");
        }
        const split = new Split(textWrapper);
        split.resize({
          tag: {
            start:
              '<span class="s__o"><span class="s__i" style="transform: translate3d(0,102%,0);">',
            end: "</span></span>",
          },
        });
      }

      // Make _in elements visible so split animation is seen
      const inEls = queryAll(this.container, "._in") as HTMLElement[];
      inEls.forEach((el) => {
        el.style.opacity = "1";
      });

      // Wrap h1 lines in split reveal wrappers
      const h1Lines = queryAll(this.container, ".l-c_t_b_h p") as HTMLElement[];
      h1Lines.forEach((p) => {
        const wrapper = document.createElement("span");
        wrapper.className = "s__o";
        const inner = document.createElement("span");
        inner.className = "s__i";
        inner.style.transform = "translate3d(0,102%,0)";
        inner.style.display = "inline-block";
        while (p.firstChild) inner.appendChild(p.firstChild);
        wrapper.appendChild(inner);
        p.appendChild(wrapper);
      });

      // Wrap info label in split reveal wrapper
      const infoP = this.container?.querySelector(
        ".l-c_t_info > p"
      ) as HTMLElement | null;
      if (infoP) {
        const wrapper = document.createElement("span");
        wrapper.className = "s__o";
        const inner = document.createElement("span");
        inner.className = "s__i";
        inner.style.transform = "translate3d(0,102%,0)";
        inner.style.display = "inline-block";
        while (infoP.firstChild) inner.appendChild(infoP.firstChild);
        wrapper.appendChild(inner);
        infoP.appendChild(wrapper);
      }

      // Wrap info email in split reveal wrapper
      const infoEmail = this.container?.querySelector(
        ".l-c_t_info_email"
      ) as HTMLElement | null;
      if (infoEmail) {
        const wrapper = document.createElement("span");
        wrapper.className = "s__o";
        const inner = document.createElement("span");
        inner.className = "s__i";
        inner.style.transform = "translate3d(0,102%,0)";
        inner.style.display = "inline-block";
        while (infoEmail.firstChild) inner.appendChild(infoEmail.firstChild);
        wrapper.appendChild(inner);
        infoEmail.appendChild(wrapper);
      }

      // Wrap each sticker link itself in split reveal wrapper
      const stickers = queryAll(
        this.container,
        ".l-c_t_info_links .sticker"
      ) as HTMLElement[];
      stickers.forEach((sticker) => {
        const wrapper = document.createElement("span");
        wrapper.className = "s__o";
        wrapper.style.overflow = "hidden";
        const inner = document.createElement("span");
        inner.className = "s__i";
        inner.style.transform = "translate3d(0,102%,0)";
        inner.style.display = "inline-block";
        sticker.parentNode!.insertBefore(wrapper, sticker);
        inner.appendChild(sticker);
        wrapper.appendChild(inner);
      });

      // Split each nav link by line
      const linkEls = queryAll(this.container, ".l-c_t_n li") as HTMLElement[];
      linkEls.forEach((li) => {
        const wrapper = document.createElement("span");
        wrapper.className = "s__o";
        const inner = document.createElement("span");
        inner.className = "s__i";
        inner.style.transform = "translate3d(0,102%,0)";
        inner.style.display = "inline-block";
        while (li.firstChild) inner.appendChild(li.firstChild);
        wrapper.appendChild(inner);
        li.appendChild(wrapper);
      });

      // Collect all split line nodes
      const splitLines = queryAll(this.container, ".s__i") as HTMLElement[];

      // Stagger-reveal each split line
      splitLines.forEach((line, i) => {
        const lineDelay = delay + 100 + i * 80;
        new Anima({
          el: line,
          p: {
            y: [102, 0, "%"],
          },
          d: 1000,
          de: lineDelay,
          e: "oQ",
        }).play();
      });

      // Resolve and activate scroll after last animation would complete
      const totalDur = delay + 100 + splitLines.length * 80 + 1000 + 50;
      setTimeout(() => {
        this._activateScroll();
        resolve();
      }, totalDur);
    });
  }

  // ── Resize ──────────────────────────────────────────────────────────

  resizePlane() {
    if (!this.plane) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (this._hasDeck && this._deckPhase < 3) {
      this._computeDeckLayout();
      const homeSpread = this._deckSpreadPositions[this._homePlaneIndex];
      if (homeSpread) {
        this._translateTarget = {
          x: homeSpread.x,
          y: homeSpread.y,
          w: homeSpread.w,
          h: homeSpread.h,
        };
      }
      if (this._slot) {
        const slotRect = bounds(this._slot);
        this._scaleTarget = {
          x: slotRect.left,
          y: slotRect.top,
          w: slotRect.width,
          h: slotRect.height,
        };
      }
    } else {
      const w = vw * 0.3;
      const h = w / (16 / 9);
      const x = vw / 2 - w / 2;
      const y = vh + h;
      this.plane.boundsPx = { x, y, w, h, z: 1 };
    }
    this.gpu?.requestExtraFrames?.();
  }

  private _resizeScaleTarget(): void {
    if (!this._slot) return;
    const slotRect = bounds(this._slot);
    this._scaleTarget = {
      x: slotRect.left,
      y: slotRect.top,
      w: slotRect.width,
      h: slotRect.height,
    };

    // Also update translateTarget so scroll scrub stays consistent
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = vw * 0.3;
    const h = w / (16 / 9);
    this._translateTarget = { x: vw / 2 - w / 2, y: vh / 2 - h / 2, w, h };
    this._translateFrom = { x: vw / 2 - w / 4, y: vh + h, w: w / 2, h: h / 2 };
    this.gpu?.requestExtraFrames?.();
  }

  // ── Scroll scrub ───────────────────────────────────────────────────

  private _activateScroll(): void {
    this._scrollActive = true;
    this._navigating = false;
    this._scrollTarget = 0;
    this._scrollCurrent = 0;
    this._lastDeltaTime = 0;

    if (this.gpu) {
      // Desktop: GPU-driven scroll scrub with plane animation
      this._resizeScaleTarget();

      if (this._roId !== null) {
        ResizeHub.remove(this._roId);
      }
      this._roId = ResizeHub.add(() => this._resizeScaleTarget());

      this.gpu.freezeBounds?.(true);

      this._wheelSub = new WheelKeySubscription({
        cb: (delta: number) => this._onWheelDelta(delta),
      });
      this._wheelSub.on();

      this._onTouchStart = (e: TouchEvent) => {
        const touch = e.touches[0];
        if (touch) this._touchStartY = touch.clientY;
      };
      this._onTouchMove = (e: TouchEvent) => {
        const touch = e.touches[0];
        if (!touch) return;
        const deltaY = this._touchStartY - touch.clientY;
        this._onWheelDelta(deltaY);
        this._touchStartY = touch.clientY;
      };
      document.addEventListener("touchstart", this._onTouchStart, {
        passive: true,
      });
      document.addEventListener("touchmove", this._onTouchMove, {
        passive: true,
      });

      this.gpu.setPreRender?.(() => this._scrollScrubFrame());
    } else {
      // No GPU: fade out and navigate via touch/wheel
      if (!Sniff.isMobile) {
        this._wheelSub = new WheelKeySubscription({
          cb: (delta: number) => this._onWheelDelta(delta),
        });
        this._wheelSub.on();
      }

      this._onTouchStart = (e: TouchEvent) => {
        const touch = e.touches[0];
        if (touch) this._touchStartY = touch.clientY;
      };
      this._onTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        const touch = e.touches[0];
        if (!touch) return;
        const deltaY = this._touchStartY - touch.clientY;
        this._onWheelDelta(deltaY);
        this._touchStartY = touch.clientY;
      };
      document.addEventListener("touchstart", this._onTouchStart, {
        passive: true,
      });
      document.addEventListener("touchmove", this._onTouchMove, {
        passive: false,
      });

      this._mobileScrollRaf = requestAnimationFrame(() =>
        this._mobileScrollFrame()
      );
    }
  }

  private _deactivateScroll(): void {
    this._wheelSub?.off();
    this._wheelSub = null;
    if (this._onTouchStart)
      document.removeEventListener("touchstart", this._onTouchStart);
    if (this._onTouchMove)
      document.removeEventListener("touchmove", this._onTouchMove);
    this._onTouchStart = null;
    this._onTouchMove = null;
    if (this._mobileScrollRaf) {
      cancelAnimationFrame(this._mobileScrollRaf);
      this._mobileScrollRaf = 0;
    }
    this._scrollActive = false;
    this._scrollTarget = 0;
    this._scrollCurrent = 0;
  }

  private get _threshold(): number {
    return Sniff.isMobile ? SCROLL_THRESHOLD_MOBILE : SCROLL_THRESHOLD;
  }

  private _onWheelDelta(delta: number): void {
    if (!this._scrollActive || this._navigating) return;
    this._scrollTarget = clamp(this._scrollTarget + delta, 0, this._threshold);
    this._lastDeltaTime = performance.now();
  }

  /**
   * Scrub the intro animation in reverse based on scroll progress.
   * progress 0      = resting state (plane at DOM slot, _scaleTarget)
   * progress 0–0.5  = reverse Phase 2: _scaleTarget → _translateTarget (+ borderRadius 0→8)
   * progress 0.5–1  = reverse Phase 1: _translateTarget → shrink + exit upward
   */
  private _scrollScrubFrame(): void {
    if (
      !this.plane?.boundsPx ||
      !this._translateFrom ||
      !this._translateTarget ||
      !this._scaleTarget
    )
      return;

    const now = performance.now();
    const ease = Ease.ioQ ?? ((t: number) => t);

    // Snap back if idle and below threshold
    if (
      this._lastDeltaTime > 0 &&
      now - this._lastDeltaTime > SCROLL_IDLE_MS &&
      this._scrollTarget < this._threshold * 0.95
    ) {
      this._scrollTarget = damp(this._scrollTarget, 0, SCROLL_SNAP_DAMP);
      if (this._scrollTarget < 0.5) this._scrollTarget = 0;
    }

    // Damp current toward target
    this._scrollCurrent = damp(
      this._scrollCurrent,
      this._scrollTarget,
      SCROLL_ACTIVE_DAMP
    );

    const progress = clamp(this._scrollCurrent / this._threshold, 0, 1);

    if (progress < 0.5) {
      // Reverse Phase 2: scaleTarget → translateTarget
      const localT = ease(progress / 0.5);
      const from = this._scaleTarget;
      const to = this._translateTarget;
      this.plane.boundsPx.x = lerp(from.x, to.x, localT);
      this.plane.boundsPx.y = lerp(from.y, to.y, localT);
      this.plane.boundsPx.w = lerp(from.w, to.w, localT);
      this.plane.boundsPx.h = lerp(from.h, to.h, localT);
      this.plane.borderRadius = lerp(0, 8, localT);
      this.plane.rotation = [0, 0, 0];
    } else {
      // Reverse Phase 1: translateTarget → shrink + exit upward
      const localT = ease((progress - 0.5) / 0.5);
      const from = this._translateTarget;
      const to = this._translateFrom;
      const w = lerp(from.w, to.w, localT);
      const h = lerp(from.h, to.h, localT);
      const cx = lerp(from.x + from.w / 4, to.x + to.w / 4, localT);
      const exitY = -to.h;
      const cy = lerp(from.y + from.h / 4, exitY, localT);
      this.plane.boundsPx.x = cx - w / 4;
      this.plane.boundsPx.y = cy - h / 4;
      this.plane.boundsPx.w = w;
      this.plane.boundsPx.h = h;
      this.plane.borderRadius = 8;
    }

    // Text parallax + fade — finish by 30% scroll progress
    const textProgress = clamp(progress / 0.3, 0, 1);
    for (const el of this._fadeEls) {
      el.style.opacity = String(1 - textProgress);
      el.style.transform = `translate3d(0, ${-(textProgress * 80)}px, 0)`;
    }

    // Bg fade — finish by 30% scroll progress
    if (this._bgEl) {
      this._bgEl.style.opacity = String(1 - textProgress);
    }

    // Trigger navigation once fully faded
    if (progress >= 0.95 && !this._navigating) {
      this._navigating = true;
      this._scrollActive = false;
      this._deactivateScroll();
      App.transitState = { slug: "", direction: "to-projects" };
      out("/projects");
      return;
    }

    // Keep rendering while scroll is active
    this.gpu?.requestExtraFrames?.();
  }

  /** No-GPU scroll scrub — fades out DOM content and navigates on threshold. */
  private _mobileScrollFrame(): void {
    if (!this._scrollActive) return;

    const now = performance.now();
    const mobile = Sniff.isMobile;
    const idleMs = mobile ? SCROLL_IDLE_MS_MOBILE : SCROLL_IDLE_MS;
    const snapDamp = mobile ? SCROLL_SNAP_DAMP_MOBILE : SCROLL_SNAP_DAMP;
    const activeDamp = mobile ? SCROLL_ACTIVE_DAMP_MOBILE : SCROLL_ACTIVE_DAMP;

    // Snap back if idle and below threshold
    if (
      this._lastDeltaTime > 0 &&
      now - this._lastDeltaTime > idleMs &&
      this._scrollTarget < this._threshold * 0.95
    ) {
      this._scrollTarget = damp(this._scrollTarget, 0, snapDamp);
      if (this._scrollTarget < 0.5) this._scrollTarget = 0;
    }

    this._scrollCurrent = damp(
      this._scrollCurrent,
      this._scrollTarget,
      activeDamp
    );

    const progress = clamp(this._scrollCurrent / this._threshold, 0, 1);

    // Fade out text + bg — text stays fixed in place on mobile
    const textProgress = clamp(progress / 0.3, 0, 1);
    for (const el of this._fadeEls) {
      el.style.opacity = String(1 - textProgress);
    }
    if (this._bgEl) {
      this._bgEl.style.opacity = String(1 - textProgress);
    }

    // Navigate as soon as raw target crosses threshold — bypass damping on mobile
    const targetProgress = clamp(this._scrollTarget / this._threshold, 0, 1);
    if ((targetProgress >= 0.95 || progress >= 0.95) && !this._navigating) {
      this._navigating = true;
      this._scrollActive = false;
      this._deactivateScroll();
      App.transitState = { slug: "", direction: "to-projects" };
      out("/projects");
      return;
    }

    this._mobileScrollRaf = requestAnimationFrame(() =>
      this._mobileScrollFrame()
    );
  }
}
